import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, User, LogOut } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { logout } from '../../features/auth/authSlice';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ProfileModal } from './ProfileModal';

export const DashboardHeader = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.auth.user);
    const [isProfileModalOpen, setIsProfileModalOpen] = React.useState(false);

    const userDisplayName = user?.name || user?.user_name || user?.username || user?.email || 'User';
    const userInitial = userDisplayName.charAt(0).toUpperCase();

    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
    };

    const getPageTitle = () => {
        const path = location.pathname;
        if (path === '/home' || path === '/') return 'Home';
        if (path.startsWith('/templates')) return 'Templates';
        if (path.startsWith('/brand-kit')) return 'Brand Kit';
        if (path.startsWith('/my-projects')) return 'My Projects';
        if (path.startsWith('/ai-generator')) return 'AI Generator';
        return 'Dashboard';
    };

    return (
        <header className="h-[72px] flex items-center justify-between px-[24px] bg-white border-b border-[#e4e7ec] shrink-0 sticky top-0 z-50">
            <h1 className="text-[20px] font-bold text-[#101928] font-sans">
                {getPageTitle()}
            </h1>

            <div className="flex items-center gap-[16px]">

                {/* Profile Dropdown */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <div className="flex items-center gap-2 cursor-pointer pl-2">
                             <div className="bg-[#2c9944] h-[36px] w-[36px] rounded-full flex items-center justify-center text-white text-[14px] font-bold">
                                {userInitial}
                            </div>
                            <div className="hidden md:flex flex-col items-start leading-tight">
                                <span className="text-[14px] font-bold text-[#101928]">{userDisplayName}</span>
                                <span className="text-[11px] text-[#667185]">Company Admin</span>
                            </div>
                            <ChevronDown className="w-4 h-4 text-[#475367] ml-1" />
                        </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px] mt-1 p-1 shadow-lg border-[#e4e7ec] bg-white">
                        <DropdownMenuLabel className="px-3 py-2">
                            <p className="text-[14px] font-bold text-[#101928] truncate">{userDisplayName}</p>
                            <p className="text-[11px] text-[#667185] truncate font-normal">Account Settings</p>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="px-3 py-2 cursor-pointer focus:bg-gray-50 rounded-md group" onClick={() => setIsProfileModalOpen(true)}>
                            <User className="w-4 h-4 mr-2 text-gray-500" />
                            <span className="text-[13px] text-gray-700">Profile Settings</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            className="px-3 py-2 cursor-pointer focus:bg-red-50 text-red-600 rounded-md"
                            onClick={handleLogout}
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            <span className="text-[13px] font-medium">Log out</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <ProfileModal
                isOpen={isProfileModalOpen}
                onClose={() => setIsProfileModalOpen(false)}
                user={user}
            />
        </header>
    );
};
